/**
 * What a tap on "Add to calendar" does, decided in one place for the sheet's
 * action bar and the overflow menu's row — Doug, 2 Oct 2026: "add ability
 * to choose the date to save to the calendar if an event has multiple
 * dates."
 *
 * Before this, both callers wrote the *next* showing on the first tap. The
 * chips were there and a tap on one did change what Calendar wrote, but
 * nothing said so, and on a row with twelve nights the tap silently booked
 * tomorrow's. So a row with more than one night ahead now asks first —
 * unless the person has already answered by tapping a chip, in which case
 * one tap stays one tap, exactly as it does on a row with a single night.
 *
 * Dependency-free on purpose, the same reason `ics.ts` is: the Worker's
 * offline suite (`test/calendarTap.test.ts`) loads this file directly under
 * `node --test`, with no build step.
 *
 * **Two inputs that look like one.** `occurrences` is how many nights are
 * ahead; `chosen` is whether the person tapped one of them. The sheet also
 * has a *selected* chip — the next night, until a tap moves it — and that
 * is deliberately not what this reads: a selected chip means "there is a
 * default", a chosen one means "the person decided". The first tap wrote
 * the wrong night precisely because the old code read the first as if it
 * were the second (CLAUDE.md, "one value doing two jobs").
 */
export type CalendarTapAction =
  /** Write the selected showing now: one night ahead, or a chosen one. */
  | 'write'
  /** Several nights ahead and none chosen: show the dates and ask. */
  | 'pick'
  /** Nothing clocked, but a window to choose a day in: open the date field. */
  | 'date-input'
  /** Nothing to write to: the run has passed, or there is no window. The
   *  callers draw the button disabled for this, so it is never reached from
   *  a tap — named so the rule is complete rather than falling through. */
  | 'passed'

export function calendarTapAction(input: {
  /** Future nights, one per night and place — `uniqueShowings(occurrencesOf(…))`
   *  after the past has been filtered off. */
  occurrences: number
  /** The person tapped a chip. Not "a chip is selected": the next night is
   *  selected by default and nobody chose it. */
  chosen: boolean
  /** A day could be picked by hand — a dated run still inside its window,
   *  or an undated row (which gets two years). */
  hasWindow: boolean
}): CalendarTapAction {
  const { occurrences, chosen, hasWindow } = input
  if (occurrences <= 0) return hasWindow ? 'date-input' : 'passed'
  if (occurrences === 1 || chosen) return 'write'
  return 'pick'
}

/** The button's own label — "Choose a date" on a row that will ask, so the
 *  icon does not promise a write it is not going to make. */
export function calendarTapLabel(action: CalendarTapAction): string {
  switch (action) {
    case 'pick': return 'Choose a date'
    case 'passed': return 'Already passed'
    default: return 'Add to calendar'
  }
}
